import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { heroSlides as defaultSlides } from '../mock';

const API = process.env.REACT_APP_BACKEND_URL;

// Convert /api/... to absolute URL
const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

export default function Hero() {
  const [slides, setSlides] = useState(defaultSlides);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/api/site/hero-slides`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setSlides(r.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;
    const t = setInterval(
      () => setActive((i) => (i + 1) % slides.length),
      6000
    );
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <section className="relative w-full h-[88vh] min-h-[600px] overflow-hidden bg-neutral-900">
      {slides.map((s, i) => {
        const key = s.id || i;
        const link = s.ctaLink || s.cta_link || '/products';
        const showOverlay = s.show_overlay !== false;
        return (
          <div
            key={key}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-out ${
              i === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center kenburns"
              style={{ backgroundImage: `url(${absUrl(s.image)})` }}
            />
            {showOverlay && (
              <>
                <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
                <div className="relative z-10 max-w-[1400px] mx-auto h-full px-6 lg:px-12 flex flex-col justify-center">
                  <div
                    className="max-w-xl text-white fade-up"
                    key={i === active ? active : 'idle'}
                  >
                    <p className="text-xs md:text-sm tracking-[0.25em] text-red-400 font-semibold mb-5">
                      {s.eyebrow}
                    </p>
                    <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
                      {s.title}
                    </h1>
                    <p className="text-base md:text-lg text-neutral-200 mb-9 leading-relaxed max-w-lg">
                      {s.subtitle}
                    </p>
                    <Link
                      to={link}
                      className="inline-flex items-center gap-2 bg-white text-neutral-900 hover:bg-px-red hover:text-white font-semibold px-7 py-3.5 rounded-full transition-all duration-300 group"
                    >
                      {s.cta}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </>
            )}
            {!showOverlay && (
              <Link
                to={link}
                className="absolute inset-0 z-10"
                aria-label={s.title || 'Banner'}
              />
            )}
          </div>
        );
      })}

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`h-1 rounded-full transition-all duration-500 ${
              i === active ? 'w-12 bg-px-red' : 'w-6 bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`slide ${i + 1}`}
          />
        ))}
      </div>

      <div className="hidden lg:flex absolute right-8 bottom-12 z-20 items-center gap-3 text-white/70 text-xs tracking-[0.3em] [writing-mode:vertical-rl]">
        <span>SCROLL</span>
        <div className="h-12 w-px bg-white/40" />
      </div>
    </section>
  );
}
